<template>
  <main class="container">
    <section class="register-container" dir="rtl">
      <img class="register-image" src="/image/background.jpg" alt="" data-not-lazy>

      <div class="register-form-wrapper">
        <h1 class="register-title">
          ثبت نام
        </h1>

        <VeeForm
          class="register-form"
          :validate-on-blur="true"
          :validate-on-change="true"
          :validate-on-input="false"
          :validate-on-model-update="true"
          @submit="onSubmit"
        >
          <div class="register-form-control">
            <VeeField
              v-model="form.lastname"
              name="lastname"
              class="register-input"
              rules="required|alpha_custom"
            />

            <label class="register-label">
              نام
            </label>

            <ErrorMessage name="lastname" as="span" class="error-message" />
          </div>

          <div class="register-form-control">
            <VeeField
              v-model="form.familyname"
              name="familyname"
              class="register-input"
              rules="required|alpha_custom"
            />

            <label class="register-label">
              نام خانوادگی
            </label>

            <ErrorMessage name="familyname" as="span" class="error-message" />
          </div>

          <div class="register-form-control">
            <VeeField
              v-model="form.phone"
              name="phone"
              class="register-input"
              type="tel"
              rules="required|phone"
            />

            <label class="register-label">
              شماره همراه
            </label>

            <ErrorMessage name="phone" as="span" class="error-message" />
          </div>

          <div class="register-form-control">
            <VeeField
              v-model="form.email"
              name="email"
              class="register-input"
              type="email"
              rules="required|email"
            />

            <label class="register-label">
              ایمیل
            </label>

            <ErrorMessage name="email" as="span" class="error-message" />
          </div>

          <div class="register-form-control">
            <VeeField
              v-model="form.password"
              name="password"
              class="register-input"
              type="password"
              rules="required|min:8|password_custom"
            />

            <label for="password" class="register-label">
              رمز
            </label>

            <ErrorMessage name="password" as="span" class="error-message" />
          </div>

          <div class="register-form-control">
            <VeeField
              v-model="form.repeat_password"
              name="repeat_password"
              class="register-input"
              type="password"
              rules="required|confirmed:password"
            />

            <label for="password" class="register-label">
              تکرار رمز
            </label>

            <ErrorMessage name="repeat_password" as="span" class="error-message" />
          </div>

          <div class="register-form-control">
            <VeeField
              v-slot="{ field }"
              name="terms"
              type="checkbox"
              :value="true"
              :unchecked-value="false"
            >
              <span class="terms-text">
                <NuxtLink to="/terms" target="_blank" style="color: #fe0100;">شرایط و قوانین فیاتر</NuxtLink> را
                می‌پذیرم و بیشتر از 15 سال سن دارم.
              </span>

              <label for="terms" class="register-label" />
            </VeeField>
          </div>

          <button type="submit" class="register-submit">ثبت نام</button>
        </VeeForm>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">

definePageMeta({
  middleware: ['redirect-if-authenticated'],
});

useSeoMeta({
  title: 'ثبت نام | فیاتر',
  description: 'صفحه ثبت نام در سایت FIATRE برای دسترسی به خدمات و محتوای ویژه.',
  keywords: 'ثبت نام, FIATRE, عضویت, خدمات ویژه, محتوای ویژه',
  ogTitle: 'ثبت نام',
  ogDescription: 'صفحه ثبت نام در سایت FIATRE برای دسترسی به خدمات و محتوای ویژه.',
  ogType: 'website',
  ogUrl: 'https://fiatre.ir/register',
  ogImage: 'https://fiatre.ir/og-image.jpg',
  robots: 'index, follow',
});

import {useValidationRules, convertPersianToEnglish} from '@/utils/validationRules.js';


import {ErrorMessage, useForm} from 'vee-validate';
import {toast} from 'vue3-toastify';
useValidationRules();

const router = useRouter();
const isLoading = ref(false);

const form = ref({
  lastname: '',
  familyname: '',
  phone: '',
  email: '',
  password: '',
  repeat_password: '',
});

const {handleSubmit} = useForm({
  initialValues: form.value,
});

const onSubmit = handleSubmit(async () => {
  
  const normalizedPhone = convertPersianToEnglish(form.value.phone);
  const normalizedPassword = convertPersianToEnglish(form.value.password);
  const normalizedPassword2 = convertPersianToEnglish(form.value.repeat_password);


  try {
    isLoading.value = true;

    const {data: registerData, error: registerError} = await useAuthFetch('auth/register/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        first_name: form.value.lastname,
        last_name: form.value.familyname,
        email: form.value.email,
        phone: normalizedPhone,
        password: normalizedPassword, 
        password2: normalizedPassword2,
        remember_me: true,
      }),
      credentials: 'include',
    });

    if (registerError.value) {
      if (registerError.value.data?.non_field_errors) {
        toast.error(registerError.value.data.non_field_errors[0]);
      } else if (registerError.value.data) {
        const messages = Object.values(registerError.value.data)
          .flat()
          .join('\n');
        toast.error(messages);
      } else {
        toast.error('خطا در ثبت نام');
      }
      return;
    }

    // If registration is successful, automatically log in
    if (registerData.value) {
      const {data: loginData, error: loginError} = await useAuthFetch('auth/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: normalizedPhone, // <-- ۶. استفاده از مقدار نرمال‌شده
          password: normalizedPassword, // <-- ۷. استفاده از مقدار نرمال‌شده
          remember_me: true,
        }),
        credentials: 'include',
      });

      if (loginError.value) {
        toast.error('خطا در ورود به سیستم. لطفا مجددا تلاش کنید.');
        return;
      }

      if (loginData.value) {
        toast.success('ثبت نام و ورود با موفقیت انجام شد');
        await navigateTo('/');
      }
    }
  } catch (err) {
    toast.error('خطا در ارتباط با سرور');
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped lang="scss">
.register-container {
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  .register-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 620px;
    object-fit: cover;
    z-index: -1;
    filter: brightness(45%);
  }

  .register-form-wrapper {
    margin-bottom: 10px;
    margin-top: 10px;
    width: 100%;
    max-width: 450px;
    padding: 0px 20px;
    background: rgba(0, 0, 0, 0.75);
    border-radius: 8px;
    text-align: center;
    position: relative;

    .register-title {
      display: flex;
      justify-content: center;
      align-items: center;
      color: $white;
      font-size: 1.3rem;
    }

    .register-form {

      .register-form-control {
        height: 45px;
        position: relative;
        margin-bottom: 25px;

        .register-input {
          height: 100%;
          width: 100%;
          border: none;
          outline: none;
          border-radius: 4px;
          color: $dark;
          font-size: 0.8rem;
          padding: 0 20px;
          direction: rtl;

          &:focus+.register-label,
          &[has-value]+.register-label {
            font-size: 0.7rem;
            transform: translateY(-100%);
          }
        }

        .register-label {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.9rem;
          pointer-events: none;
          color: $gray;
          transition: all 0.1s ease;
          display: flex;
        }

        .error-message {
          color: $danger;
          font-size: 0.8rem;
          margin-top: 0.25rem;
        }
      }

      .register-submit {
        width: 100%;
        padding: 14px 0;
        font-size: 0.9rem;
        background: $third;
        color: $white;
        font-weight: 500;
        border-radius: 4px;
        border: none;
        outline: none;
        cursor: pointer;
        transition: 0.1s ease;
        margin-bottom: 10px;

        &:hover {
          background: darken($third, 10);
        }
      }
    }

    .terms-text {
      color: #797979;
      display: block;
      margin-bottom: 10px;
    }
  }
}

// Add responsive styles for mobile devices
@media (max-width: 500px) {

  .register-form-wrapper {
    max-width: 80% !important; // Reduce the max-width for better fit
    // Reduce padding inside the form

    .register-title {
      font-size: 1.1rem !important; // Slightly smaller title
      margin-left: 10%;
      white-space: nowrap !important;
    }

    .register-form {
      .register-form-control {
        height: 35px !important; // Reduce input height
        margin-bottom: 25px !important; // Reduce margin between inputs

        .register-input {
          font-size: 0.8rem !important; // Smaller font size for input text
          padding: 0 15px !important; // Reduce padding inside inputs

          &:focus+.register-label,
          &[has-value]+.register-label {
            font-size: 0.6rem !important; // Smaller font for focused/filled labels
            transform: translateY(-100%);
          }
        }

        .register-label {
          font-size: 0.8rem !important; // Smaller label font size
        }

        .error-message {
          font-size: 0.6rem !important;
          padding-bottom: 5px !important;
        }
      }

      .register-submit {
        padding: 12px 0 !important; // Reduce button height
        margin-bottom: 10px;
        font-size: 0.8rem !important; // Smaller font size for button text
      }
    }

    .terms-text {
      font-size: 0.7rem !important; // Smaller font size for terms text
      line-height: 1.25 !important; // Adjust line spacing for readability
    }
  }
}

@media (max-width: 360px) {
  .register-form-wrapper {
    .register-title {
      font-size: 0.9rem;
    }

    .register-form-control {
      height: 35px;
      margin-bottom: 15px;
    }
  }
}
</style>
